import { Box, GitBranch, ArrowRight, Zap } from "lucide-react";
import { ResizablePanel } from "../ui/resizable";
import { ScrollArea } from "../ui/scroll-area";
import type { Id } from "@/convex/src/_generated/dataModel";

interface InteractionModule {
  _id: Id<"interactionModules">;
  name: string;
  slug: string;
  description?: string;
}

interface FlowGraphData {
  flow: {
    _id: Id<"flows">;
    name: string;
  };
  nodes: Array<{
    _id: Id<"flowInteractionModules">;
    interactionModuleId: Id<"interactionModules">;
  }>;
  edges: Array<{
    _id: Id<"flowEdges">;
    driver: { _id: Id<"flowDrivers"> } | null;
  }>;
  interactionModules: InteractionModule[];
}

interface FlowSummaryPanelProps {
  flowGraph: FlowGraphData | null | undefined;
}

export function FlowSummaryPanel({ flowGraph }: FlowSummaryPanelProps) {
  if (!flowGraph) {
    return (
      <ResizablePanel defaultSize={40} minSize={20}>
        <div className="h-full flex flex-col border-r border-border bg-background">
          <div className="px-4 py-3 border-b border-border">
            <h3 className="text-xs font-semibold text-foreground">
              Flow Summary
            </h3>
          </div>
          <div className="flex-1 flex items-center justify-center">
            <p className="text-xs text-muted-foreground">
              Select a flow to see summary
            </p>
          </div>
        </div>
      </ResizablePanel>
    );
  }

  const { nodes, edges, interactionModules } = flowGraph;
  const driverCount = edges.filter((e) => e.driver).length;

  return (
    <ResizablePanel defaultSize={40} minSize={20}>
      <div className="h-full flex flex-col border-r border-border bg-background">
        <div className="px-4 py-3 border-b border-border">
          <h3 className="text-xs font-semibold text-foreground">
            Flow Summary
          </h3>
        </div>

        <div className="flex-1 overflow-hidden">
          <ScrollArea className="h-full">
            <div className="p-4 space-y-6">
              {/* Counts Section */}
              <div>
                <h4 className="text-xs font-medium text-muted-foreground mb-3">
                  Statistics
                </h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="flex flex-col items-center p-3 bg-muted/30 rounded-md border border-border">
                    <GitBranch className="h-4 w-4 text-muted-foreground mb-1" />
                    <span className="text-lg font-semibold">
                      {nodes.length}
                    </span>
                    <span className="text-xs text-muted-foreground">Nodes</span>
                  </div>
                  <div className="flex flex-col items-center p-3 bg-muted/30 rounded-md border border-border">
                    <ArrowRight className="h-4 w-4 text-muted-foreground mb-1" />
                    <span className="text-lg font-semibold">
                      {edges.length}
                    </span>
                    <span className="text-xs text-muted-foreground">Edges</span>
                  </div>
                  <div className="flex flex-col items-center p-3 bg-muted/30 rounded-md border border-border">
                    <Zap className="h-4 w-4 text-muted-foreground mb-1" />
                    <span className="text-lg font-semibold">{driverCount}</span>
                    <span className="text-xs text-muted-foreground">
                      Drivers
                    </span>
                  </div>
                </div>
              </div>

              {/* Modules Section */}
              <div>
                <h4 className="text-xs font-medium text-muted-foreground mb-3">
                  Interaction Modules ({interactionModules.length})
                </h4>
                {interactionModules.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    No modules in this flow.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {interactionModules.map((mod) => (
                      <div
                        key={mod._id}
                        className="p-2 bg-muted/20 rounded-md border border-border"
                      >
                        <div className="flex items-center gap-2">
                          <Box className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                          <span className="text-sm font-medium truncate">
                            {mod.name}
                          </span>
                        </div>
                        <code className="text-xs text-muted-foreground font-mono ml-5">
                          {mod.slug}
                        </code>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </ScrollArea>
        </div>
      </div>
    </ResizablePanel>
  );
}
